import { motion } from "framer-motion";
import { Star, Quote, Calendar, CheckCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { isRTL, useLanguageStore } from "@/stores/languageStore";

interface Testimonial {
  id: number;
  name: string;
  content: string;
  rating: number;
  product?: string;
  initials: string;
  date: string;
  verified: boolean;
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    name: "יוסי כהן",
    content: "אתר מטורף! מבחר מוצרים רחב, מחירים מצחיקים והמשלוח הגיע תוך 3 ימים. בהחלט אחזור לקנות עוד!",
    rating: 5,
    initials: "יכ",
    date: "15.01.2026",
    verified: true
  },
  {
    id: 2,
    name: "מיכל שרון",
    content: "חוויית קנייה נוחה ובטוחה. האתר מאורגן יפה, קל למצוא מה שרוצים, והמחירים באמת הכי זולים שמצאתי.",
    rating: 5,
    initials: "מש",
    date: "12.01.2026",
    verified: true
  },
  {
    id: 3,
    name: "דוד אברהמי",
    content: "הזמנתי כמה מוצרים והכל הגיע מהר ובאריזה מושלמת. השירות היה מעולה וענו לי על כל שאלה. ממליץ!",
    rating: 4.5,
    initials: "דא",
    date: "10.01.2026",
    verified: true
  },
  {
    id: 4,
    name: "רונית לוי",
    content: "קניתי את כיסוי הרכב האוטומטי - פשוט גאוני! חוסך לי זמן כל יום והרכב תמיד נקי.",
    rating: 5,
    product: "כיסוי רכב אוטומטי",
    initials: "רל",
    date: "08.01.2026",
    verified: true
  },
  {
    id: 5,
    name: "אבי מזרחי",
    content: "המצלמה עובדת מעולה! צילום ברור גם בלילה ושקט נפשי מלא בזמן נהיגה.",
    rating: 5,
    product: "מצלמת דרך 3 עדשות",
    initials: "אמ",
    date: "05.01.2026",
    verified: true
  },
  {
    id: 6,
    name: "שירה בן-דוד",
    content: "בקבוק המים לכלב שלי הוא הכי שווה! כבר לא צריכה לדאוג לשתייה בטיולים.",
    rating: 4.5,
    product: "בקבוק מים לכלבים",
    initials: "שב",
    date: "03.01.2026",
    verified: true
  },
  {
    id: 7,
    name: "יעקב גולן",
    content: "נייר השיוף באיכות מעולה. קניתי 50 יחידות ועובד מצוין על כל סוגי העץ. מחיר משתלם!",
    rating: 5,
    product: "נייר שיוף",
    initials: "יג",
    date: "02.01.2026",
    verified: true
  },
  {
    id: 8,
    name: "נועה ישראלי",
    content: "המאכיל האוטומטי שינה לי את החיים! עכשיו אני יכולה לצאת לעבודה בלי לדאוג לחתולים שלי.",
    rating: 4.5,
    product: "מאכיל חיות מחמד",
    initials: "ני",
    date: "28.01.2026",
    verified: true
  },
  {
    id: 9,
    name: "אלון פרץ",
    content: "המחזיק הביטים המגנטי הוא must have! חוסך לי המון זמן בעבודות הרכבה.",
    rating: 5,
    product: "מחזיק ביטים מגנטי",
    initials: "אפ",
    date: "25.01.2026",
    verified: true
  },
];

export const TestimonialsSection = () => {
  const { t, language } = useLanguageStore();
  const rtl = isRTL(language);

  return (
    <section className="py-16 bg-gradient-to-b from-muted/30 to-background">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <Badge className="mb-4 bg-accent/10 text-accent-foreground border-accent/20">
            {t('testimonials.badge') || 'מה הלקוחות אומרים'}
          </Badge>
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
            {t('testimonials.title') || 'לקוחות מרוצים'}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t('testimonials.description') || 'אלפי לקוחות כבר נהנים מהמוצרים שלנו - הצטרפו גם אתם!'}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, idx) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
            >
              <Card className="h-full border-border/50 hover:border-primary/20 hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-card to-card/80">
                <CardContent className="p-6">
                  {/* Quote Icon */}
                  <Quote className="w-8 h-8 text-primary/20 mb-4" />
                  
                  {/* Rating */}
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => {
                      const isFull = star <= Math.floor(testimonial.rating);
                      const isHalf = star === Math.ceil(testimonial.rating) && testimonial.rating % 1 !== 0;
                      return (
                        <div key={star} className="relative w-4 h-4">
                          <Star className="w-4 h-4 text-muted-foreground/30 absolute" />
                          {isFull && <Star className="w-4 h-4 fill-amber-400 text-amber-400 absolute" />}
                          {isHalf && (
                            <div className="absolute overflow-hidden w-2 h-4">
                              <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <span className="text-sm text-muted-foreground mr-1">{testimonial.rating}</span>
                  </div>

                  {/* Content */}
                  <p className="text-foreground/80 leading-relaxed mb-4">
                    "{testimonial.content}"
                  </p>

                  {/* Product Badge */}
                  {testimonial.product && (
                    <Badge variant="secondary" className="mb-4 text-xs bg-primary/10 text-primary border-primary/20">
                      {testimonial.product}
                    </Badge>
                  )}

                  {/* Author */}
                  <div className="flex items-center justify-between pt-4 border-t border-border/50">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-10 h-10 bg-gradient-to-br from-primary/20 to-primary/10 border-2 border-primary/20">
                        <AvatarFallback className="text-primary font-semibold text-sm bg-transparent">
                          {testimonial.initials}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-semibold text-foreground">{testimonial.name}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Calendar className="w-3 h-3" />
                          <span>{testimonial.date}</span>
                          {testimonial.verified && (
                            <>
                              <CheckCircle className="w-3 h-3 text-emerald-500" />
                              <span className="text-emerald-600">{t('testimonials.verified') || 'מאומת'}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
